/**
 * DeviationDashboard.jsx
 */

import { useCallback, useState, useMemo, useRef, useEffect } from "react";
import * as XLSX from "xlsx";

import { useFinancialYears, useSources } from "../../../../../../hooks/useApiData";
import { useDeviationRecords }           from "./useDeviationData";
import deviationConfig, { MONTHS }       from "./config/deviation.config";
import { BarChart as SharedBarChart } from "../../../../../ChartUtility/charts/BarChart";
import { HorizontalBarChart as SharedHorizontalBarChart } from "../../../../../ChartUtility/charts/HorizontalBarChart";
import { DonutChart as SharedDonutChart } from "../../../../../ChartUtility/charts/PieDonutChart";

const C = {
  bg: "#eef2f7", card: "#fff", border: "#dde3ed",
  accent: "#2b7a9e", accentD: "#1d5f7a", accentLight: "#e3f2f9",
  red: "#dc2626",   redLight: "#fef2f2",
  amber: "#d97706", amberLight: "#fffbeb",
  green: "#16a34a", greenLight: "#f0fdf4",
  purple: "#7c3aed", purpleLight: "#f5f3ff",
  teal: "#0f766e", tealLight: "#f0fdfa",
  slate: "#475569", slateLight: "#f8fafc",
  text: "#0f172a",  sub: "#475569", muted: "#94a3b8",
};

const sMeta = (s) => ({
  "Above Max": { color: C.red,   bg: C.redLight,   icon: "▲", label: "Above Max" },
  "Below Min": { color: C.amber, bg: C.amberLight,  icon: "▼", label: "Below Min" },
  "Normal":    { color: C.green, bg: C.greenLight,  icon: "●", label: "Normal"    },
}[s] ?? {});

const fmt2 = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
};

const PINNED_LOCATION_CODES = ["MHB", "MHMR", "MHVR", "MHYPR", "MHHBL"];
const MONTH_ORDER = MONTHS.reduce((acc, month, index) => {
  acc[month] = index;
  return acc;
}, {});

const buildRunningThresholdRows = (rows = []) => {
  const groupedRows = new Map();

  rows.forEach((row) => {
    if (!row?.hasThreshold) return;
    const min = Number(row.min);
    const max = Number(row.max);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;

    const key = [
      row.fy || "",
      row.locId || "",
      row.module || "",
      row.category || "",
      row.subCategory || "",
      row.kpi || "",
      min,
      max,
    ].join("||");

    if (!groupedRows.has(key)) groupedRows.set(key, []);
    groupedRows.get(key).push(row);
  });

  return Array.from(groupedRows.values()).flatMap((grouped) => {
    const orderedRows = [...grouped].sort((a, b) => {
      const monthDiff = (MONTH_ORDER[a.month] ?? Number.MAX_SAFE_INTEGER) - (MONTH_ORDER[b.month] ?? Number.MAX_SAFE_INTEGER);
      if (monthDiff !== 0) return monthDiff;
      return String(a.kpi || "").localeCompare(String(b.kpi || ""));
    });

    let runningValue = 0;
    return orderedRows.map((row) => {
      const actual = Number(row.actual) || 0;
      const min = Number(row.min);
      const max = Number(row.max);
      runningValue += actual;

      let status = "Normal";
      let deviation = 0;

      if (runningValue > max) {
        status = "Above Max";
        deviation = runningValue - max;
      } else if (runningValue < min) {
        status = "Below Min";
        deviation = min - runningValue;
      }

      return {
        ...row,
        actualMonthValue: actual,
        runningValue,
        status,
        deviation,
      };
    });
  });
};

const Shimmer = ({ width = "100%", height = 20, radius = 6 }) => (
  <div style={{
    width, height, borderRadius: radius,
    background: "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
    backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite",
  }} />
);

const ChartLoading = ({ height = 230 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
    <Shimmer height={10} width="25%" />
    <Shimmer height={height} />
  </div>
);

function Dropdown({ label, options, value, onChange, placeholder = "Select...", width = 200 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", width }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        width: "100%", padding: "8px 12px", background: "#fff",
        border: `1.5px solid ${open ? C.accent : C.border}`, borderRadius: 8,
        cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8,
        boxShadow: open ? `0 0 0 3px ${C.accent}20` : "none", transition: "all .15s",
      }}>
        <span style={{ flex: 1, textAlign: "left", fontSize: 13, color: value ? C.text : C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        <span style={{ color: C.muted, fontSize: 10, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%", zIndex: 300, background: "#fff", border: `1.5px solid ${C.border}`, borderRadius: 10, boxShadow: "0 10px 30px #0000001a", overflow: "hidden" }}>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <div onClick={() => { onChange(null); setOpen(false); }} style={{ padding: "8px 13px", cursor: "pointer", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, background: "#f8fafc" }}>
              All {label}
            </div>
            {options.map((o) => (
              <div key={o} onClick={() => { onChange(o); setOpen(false); }} style={{ padding: "8px 13px", cursor: "pointer", fontSize: 12, color: o === value ? C.accent : C.text, background: o === value ? C.accentLight : "#fff", fontWeight: o === value ? 700 : 400 }}>
                {o}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ label, options = [], value = [], onChange, placeholder = "Select...", showCount = false, pinToBottom = [] }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((o) => String(o || "").toLowerCase().includes(search.toLowerCase()));
  const allSelected = options.length > 0 && value.length === options.length;
  const pinnedLookup = useMemo(() => new Set(pinToBottom.map((item) => String(item || "").trim().toLowerCase())), [pinToBottom]);
  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aPinned = pinnedLookup.has(String(a || "").trim().toLowerCase());
      const bPinned = pinnedLookup.has(String(b || "").trim().toLowerCase());
      if (aPinned !== bPinned) return aPinned ? 1 : -1;
      return options.indexOf(a) - options.indexOf(b);
    });
  }, [filtered, options, pinnedLookup]);
  const toggle = (v) => {
    const next = value.includes(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange(next);
  };

  const displayText = () => {
    if (!value.length) return placeholder;
    if (value.length === options.length) return `All ${label}`;
    if (value.length === 1) return value[0];
    if (showCount && value.length > 2) return `${value.length} selected`;
    return value.slice(0, 2).join(", ") + (value.length > 2 ? ` +${value.length - 2}` : "");
  };

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 160 }}>
      <button type="button" onClick={() => setOpen((p) => !p)} style={{
        width: "100%", padding: "7px 10px", border: `1px solid ${open ? C.accent : C.border}`,
        borderRadius: 6, background: "#fff", cursor: "pointer", display: "flex",
        alignItems: "center", justifyContent: "space-between", gap: 6, fontSize: 13,
        color: value.length ? C.text : C.muted, boxShadow: open ? `0 0 0 3px ${C.accent}20` : "none", transition: "all 0.15s",
      }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayText()}</span>
        <span style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%",
          background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 1000, overflow: "hidden",
          maxHeight: 280, display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}>
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..."
              style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 5, padding: "5px 8px", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
          </div>
          <button
            type="button"
            onClick={() => onChange(allSelected ? [] : [...options])}
            style={{
              textAlign: "left",
              padding: "8px 12px",
              border: "none",
              background: "none",
              fontSize: 12,
              cursor: "pointer",
              color: C.accent,
              fontWeight: 600,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
          <div style={{ overflowY: "auto", flexGrow: 1 }}>
            {sortedFiltered.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: C.muted }}>No results</div>}
            {sortedFiltered.map((o) => {
              const checked = value.includes(o);
              return (
                <button key={o} type="button" onClick={() => toggle(o)} style={{
                  width: "100%", textAlign: "left", padding: "8px 12px", border: "none",
                  background: checked ? C.accentLight : "#fff", color: C.text, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8, fontSize: 13,
                }}>
                  <input type="checkbox" checked={checked} readOnly style={{ width: 16, height: 16, accentColor: C.accent }} />
                  <span>{o}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color, bg, icon, badge, loading }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 18px", flex: 1, minWidth: 130, borderLeft: `4px solid ${color}`, boxShadow: "0 1px 6px #0000000a", display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}</span>
        <span style={{ background: bg, borderRadius: 7, padding: "3px 7px", fontSize: 14 }}>{icon}</span>
      </div>
      {loading ? <Shimmer height={32} width="55%" radius={6} /> : <div style={{ fontSize: 22, fontWeight: 900, color, letterSpacing: -0.8, lineHeight: 1, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>}
      {sub && <div style={{ fontSize: 10, color: C.muted }}>{sub}</div>}
      {badge && !loading && (
        <div style={{ marginTop: 5, display: "inline-flex", alignItems: "center", gap: 4, background: bg, borderRadius: 20, padding: "2px 8px", alignSelf: "flex-start", border: `1px solid ${color}33` }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />
          <span style={{ fontSize: 9, fontWeight: 700, color }}>{badge}</span>
        </div>
      )}
    </div>
  );
}

function BreachBarList({ rows, labelKey, emptyText }) {
  const data = rows.filter((r) => r.above > 0 || r.below > 0 || r.normal > 0);
  if (!data.length) return (
    <div style={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: C.muted }}>{emptyText}</div>
  );
  const maxTotal = Math.max(...data.map((r) => r.above + r.below + r.normal), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 2 }}>
        {[{ label: "Above Max", color: C.red }, { label: "Below Min", color: C.amber }, { label: "Within Range", color: C.green }].map((s) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: C.sub }}>{s.label}</span>
          </div>
        ))}
      </div>
      {data.map((row) => {
        const total = Math.max(1, row.above + row.below + row.normal);
        const abovePct  = (row.above  / total) * 100;
        const belowPct  = (row.below  / total) * 100;
        const normalPct = (row.normal / total) * 100;
        const barWidthPct = (total / maxTotal) * 100;
        return (
          <div key={row[labelKey]} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text, lineHeight: 1.4, flex: 1, wordBreak: "break-word" }}>{row[labelKey]}</span>
              <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                {row.above  > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.red   }}>▲{row.above}</span>}
                {row.below  > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.amber }}>▼{row.below}</span>}
                {row.normal > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>●{row.normal}</span>}
              </div>
            </div>
            <div style={{ height: 10, background: "#eef2f7", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${barWidthPct}%`, height: "100%", display: "flex", borderRadius: 999, overflow: "hidden" }}>
                {abovePct  > 0 && <div style={{ width: `${abovePct}%`,  background: C.red,   flexShrink: 0 }} />}
                {belowPct  > 0 && <div style={{ width: `${belowPct}%`,  background: C.amber, flexShrink: 0 }} />}
                {normalPct > 0 && <div style={{ width: `${normalPct}%`, background: C.green, flexShrink: 0 }} />}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Top5({ title, subtitle, items, color, bg }) {
  const max = items[0]?.total || 1;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 6px #0000000a" }}>
      <h3 style={{ margin: "0 0 3px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>{title}</h3>
      {subtitle && <p style={{ margin: "0 0 14px", fontSize: 10, color: C.muted }}>{subtitle}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.slice(0, 5).map((item, i) => (
          <div key={item.key}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 20, height: 20, borderRadius: 6, background: bg, color, fontSize: 10, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.text, wordBreak: "break-word" }}>{item.key}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {item.above !== undefined && <span style={{ fontSize: 10, color: C.red,   fontWeight: 600 }}>▲{item.above}</span>}
                {item.below !== undefined && <span style={{ fontSize: 10, color: C.amber, fontWeight: 600 }}>▼{item.below}</span>}
                {!(item.above !== undefined && item.below === undefined) && !(item.below !== undefined && item.above === undefined) && (
                  <span style={{ fontSize: 12, fontWeight: 800, color, minWidth: 28, textAlign: "right" }}>{item.total}</span>
                )}
              </div>
            </div>
            <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 3, background: `linear-gradient(to right,${color},${color}aa)`, width: `${Math.round(item.total / max * 100)}%`, transition: "width .4s" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  const [page, setPage] = useState(0);
  const PER = 20;
  const rows = useMemo(() => {
    const grouped = {};
    data.forEach((d) => {
      const key = `${d.loc}||${d.kpi}`;
      if (!grouped[key]) grouped[key] = { loc: d.loc, kpi: d.kpi, a: 0, b: 0, n: 0 };
      if (d.status === "Above Max") grouped[key].a++;
      else if (d.status === "Below Min") grouped[key].b++;
      else grouped[key].n++;
    });
    return Object.values(grouped).map((row) => ({ ...row, total: row.a + row.b + row.n }))
      .sort((a, b) => b.total - a.total || a.loc.localeCompare(b.loc) || a.kpi.localeCompare(b.kpi));
  }, [data]);
  useEffect(() => setPage(0), [data]);
  const statusStyles = {
    above:  { bg: "#fff5f5", border: "#fca5a5", text: "#ef4444", dot: "#ef4444" },
    below:  { bg: "#fffaf0", border: "#fcd34d", text: "#d97706", dot: "#f59e0b" },
    normal: { bg: "#f0fdf4", border: "#86efac", text: "#16a34a", dot: "#22c55e" },
  };
  const totalSummary = useMemo(() => data.reduce((acc, d) => {
    if (d.status === "Above Max") acc.a += 1;
    else if (d.status === "Below Min") acc.b += 1;
    else acc.n += 1;
    return acc;
  }, { a: 0, b: 0, n: 0 }), [data]);
  const total = rows.length, pages = Math.ceil(total / PER), paged = rows.slice(page * PER, page * PER + PER);
  if (!rows.length) return <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "40px 0" }}>No location-KPI deviation data found for the current selection.</div>;
  return (
    <div style={{ overflow: "auto", maxHeight: 640, border: `1px solid ${C.border}`, borderRadius: 16, background: "#fff", boxShadow: "0 1px 6px #00000008" }}>
      <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 11, minWidth: "100%" }}>
        <thead>
          <tr>
            {["Location", "KPI", "Above", "Below", "Within", "Total"].map((label, idx) => (
              <th key={label} style={{ padding: "16px 18px", color: idx <= 1 ? C.muted : C.sub, textAlign: idx <= 1 ? "left" : "center", position: "sticky", top: 0, background: "#fff", zIndex: 3, borderBottom: "2px solid #f1f5f9", whiteSpace: "nowrap", fontSize: 11, fontWeight: 800, minWidth: idx === 0 ? 180 : idx === 1 ? 180 : 110, letterSpacing: 1, textTransform: "uppercase" }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paged.map((row) => (
            <tr key={`${row.loc}||${row.kpi}`}>
              <td style={{ padding: "14px 18px", color: C.text, fontSize: 13, background: "#fff", borderBottom: "1px solid #f1f5f9", fontWeight: 700 }}>{row.loc}</td>
              <td style={{ padding: "14px 18px", color: C.sub, fontSize: 12, background: "#fff", borderBottom: "1px solid #f1f5f9", fontWeight: 600 }}>{row.kpi}</td>
              {[
                { value: row.a, style: statusStyles.above, icon: "▲" },
                { value: row.b, style: statusStyles.below, icon: "▼" },
                { value: row.n, style: statusStyles.normal, icon: "●" },
              ].map((item, idx) => (
                <td key={idx} style={{ padding: "14px 8px", textAlign: "center", background: "#fff", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: item.value ? item.style.text : "#94a3b8", background: item.value ? item.style.bg : "#f8fafc", border: `1px solid ${item.value ? item.style.border : "#e2e8f0"}`, borderRadius: 12, padding: "9px 14px", minWidth: 82, justifyContent: "center", fontWeight: 700 }}>
                    <span style={{ fontSize: item.icon === "●" ? 11 : 10 }}>{item.icon}</span>
                    {item.value}
                  </span>
                </td>
              ))}
              <td style={{ padding: "14px 12px", textAlign: "center", background: "#fff", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ minWidth: 60, height: 40, margin: "0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 14, background: "#f1f5f9", color: "#334155", fontSize: 16, fontWeight: 800, padding: "0 14px" }}>{row.total}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", padding: "18px 22px", borderTop: "1px solid #f1f5f9", background: "#fff" }}>
        {[["above", "Above Max"], ["below", "Below Min"], ["normal", "Normal"]].map(([k, l]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.sub }}>
            <span style={{ width: 12, height: 12, borderRadius: "50%", background: statusStyles[k].dot }} />
            <span>{l}: <strong style={{ color: statusStyles[k].text }}>{totalSummary[k[0]]}</strong></span>
          </div>
        ))}
      </div>
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 22px 18px", background: "#fff" }}>
          <span style={{ fontSize: 11, color: C.muted }}>{page * PER + 1}–{Math.min((page + 1) * PER, total)} of {total.toLocaleString()}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: page === 0 ? "#f8fafc" : "#fff", cursor: page === 0 ? "default" : "pointer", fontSize: 11, color: page === 0 ? C.muted : C.accent, fontFamily: "inherit" }}>← Prev</button>
            <button disabled={page === pages - 1} onClick={() => setPage((p) => p + 1)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: page === pages - 1 ? "#f8fafc" : "#fff", cursor: page === pages - 1 ? "default" : "pointer", fontSize: 11, color: page === pages - 1 ? C.muted : C.accent, fontFamily: "inherit" }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertTable({ data }) {
  const [page, setPage] = useState(0);
  const PER = 15;
  useEffect(() => setPage(0), [data]);
  const rows = data;
  const total = rows.length, pages = Math.ceil(total / PER), paged = rows.slice(page * PER, page * PER + PER);
  const aboveTotal = rows.filter((d) => d.status === "Above Max").length;
  const belowTotal = rows.filter((d) => d.status === "Below Min").length;
  const handleDownloadExcel = () => {
    const excelData = rows.map((row, index) => ({
      "#": index + 1, Location: row.loc, Period: row.month, Module: row.module,
      "Sub Category": row.subCategory, KPI: row.kpi, Min: row.min ?? "—", Max: row.max ?? "—",
      Actual: row.actual, Deviation: fmt2(row.deviation),
      Status: row.status === "Above Max" ? "Above Trigger" : "Below Trigger",
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Alert Log");
    XLSX.writeFile(workbook, `Alert_Log_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[{ l: "Total Breaches", v: total, c: C.accent, bg: C.accentLight }, { l: "Above Max", v: aboveTotal, c: C.red, bg: C.redLight }, { l: "Below Min", v: belowTotal, c: C.amber, bg: C.amberLight }].map((p) => (
            <div key={p.l} style={{ background: p.bg, border: `1px solid ${p.c}33`, borderRadius: 20, padding: "4px 14px", display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: p.c }} />
              <span style={{ fontSize: 12, color: p.c, fontWeight: 700 }}>{p.v.toLocaleString()}</span>
              <span style={{ fontSize: 11, color: C.sub }}>{p.l}</span>
            </div>
          ))}
        </div>
        <button onClick={handleDownloadExcel} disabled={!rows.length} style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: rows.length ? "#fff" : "#f8fafc", color: rows.length ? C.accent : C.muted, fontSize: 11, fontWeight: 700, cursor: rows.length ? "pointer" : "default", fontFamily: "inherit", whiteSpace: "nowrap" }}>Download Excel</button>
      </div>
      <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["#","Location","Period","Module","Sub Category","KPI","Min","Max","Actual","Deviation","Status"].map((h) => (
                <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 9, fontWeight: 700, color: C.muted, borderBottom: `1.5px solid ${C.border}`, letterSpacing: 0.8, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((d, i) => {
              const m = sMeta(d.status);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "8px 12px", color: C.muted, fontSize: 10 }}>{page * PER + i + 1}</td>
                  <td style={{ padding: "8px 12px", color: C.text, fontWeight: 600, whiteSpace: "nowrap" }}>{d.loc}</td>
                  <td style={{ padding: "8px 12px", color: C.sub, whiteSpace: "nowrap", fontSize: 11 }}>{d.month}</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}><span style={{ background: C.accentLight, color: C.accent, borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>{d.module}</span></td>
                  <td style={{ padding: "8px 12px", color: C.sub, whiteSpace: "nowrap", fontSize: 11 }}>{d.subCategory}</td>
                  <td style={{ padding: "8px 12px", color: C.text, fontWeight: 600, whiteSpace: "nowrap" }}>{d.kpi}</td>
                  <td style={{ padding: "8px 12px" }}>{d.min != null ? <span style={{ background: C.amberLight, color: C.amber, fontWeight: 700, borderRadius: 5, padding: "1px 7px", fontSize: 10 }}>{d.min}</span> : <span style={{ color: C.muted, fontSize: 10 }}>—</span>}</td>
                  <td style={{ padding: "8px 12px" }}>{d.max != null ? <span style={{ background: C.redLight, color: C.red, fontWeight: 700, borderRadius: 5, padding: "1px 7px", fontSize: 10 }}>{d.max}</span> : <span style={{ color: C.muted, fontSize: 10 }}>—</span>}</td>
                  <td style={{ padding: "8px 12px", fontWeight: 800, color: C.text }}>{fmt2(d.actual)}</td>
                  <td style={{ padding: "8px 12px", fontWeight: 700, color: m.color, fontVariantNumeric: "tabular-nums" }}>{fmt2(d.deviation)}</td>
                  <td style={{ padding: "8px 12px" }}><span style={{ background: m.bg, color: m.color, fontWeight: 700, borderRadius: 20, padding: "2px 9px", fontSize: 10, border: `1px solid ${m.color}44`, whiteSpace: "nowrap" }}>{d.status === "Above Max" ? "Above Trigger" : "Below Trigger"}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <span style={{ fontSize: 11, color: C.muted }}>{page * PER + 1}–{Math.min((page + 1) * PER, total)} of {total.toLocaleString()}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: page === 0 ? "#f8fafc" : "#fff", cursor: page === 0 ? "default" : "pointer", fontSize: 11, color: page === 0 ? C.muted : C.accent, fontFamily: "inherit" }}>← Prev</button>
            <button disabled={page === pages - 1} onClick={() => setPage((p) => p + 1)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: page === pages - 1 ? "#f8fafc" : "#fff", cursor: page === pages - 1 ? "default" : "pointer", fontSize: 11, color: page === pages - 1 ? C.muted : C.accent, fontFamily: "inherit" }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ComparisonView({ data, compareBy, setCompareBy, fyOptions, defaultFY, locationOptions, monthOptions, g1, setG1, g2, setG2, commonLoc, setCommonLoc, commonPeriod, setCommonPeriod, commonFY, setCommonFY, loading = false }) {
  const compareOptions = compareBy === "Financial Year" ? fyOptions : compareBy === "Period" ? monthOptions : locationOptions;
  const handleCommonFYChange = useCallback((nextFY) => {
    setCommonFY(nextFY || defaultFY || null);
  }, [defaultFY, setCommonFY]);

  const groupData = (groupVal) => {
    if (!groupVal) return [];
    const filteredRows = data.filter((d) => {
      if (compareBy === "Financial Year" && d.fy    !== groupVal) return false;
      if (compareBy === "Period"         && d.month !== groupVal) return false;
      if (compareBy === "Location"       && d.loc   !== groupVal) return false;
      if (commonLoc.length    && compareBy !== "Location" && !commonLoc.includes(d.loc)) return false;
      if (commonPeriod.length && compareBy !== "Period"   && !commonPeriod.includes(d.month)) return false;
      if (commonFY     && compareBy !== "Financial Year" && d.fy !== commonFY) return false;
      return true;
    });
    return buildRunningThresholdRows(filteredRows);
  };

  const g1Data = useMemo(() => groupData(g1), [g1, commonLoc, commonPeriod, commonFY, data, compareBy]); // eslint-disable-line
  const g2Data = useMemo(() => groupData(g2), [g2, commonLoc, commonPeriod, commonFY, data, compareBy]); // eslint-disable-line

  const buildKpiChart = (d1, d2) => {
    const keyOf = (d) => d.kpi || d.subCategory || d.category || "";
    const kpis = [...new Set([...d1.map(keyOf), ...d2.map(keyOf)].filter(Boolean))];
    return kpis.map((kpi) => ({
      kpi,
      "G1 Above": d1.filter((d) => keyOf(d) === kpi && d.status === "Above Max").length,
      "G1 Below": d1.filter((d) => keyOf(d) === kpi && d.status === "Below Min").length,
      "G2 Above": d2.filter((d) => keyOf(d) === kpi && d.status === "Above Max").length,
      "G2 Below": d2.filter((d) => keyOf(d) === kpi && d.status === "Below Min").length,
    })).sort((a, b) => (b["G1 Above"] + b["G1 Below"] + b["G2 Above"] + b["G2 Below"]) - (a["G1 Above"] + a["G1 Below"] + a["G2 Above"] + a["G2 Below"])).slice(0, 10);
  };
  const buildLocChart = (d1, d2) => {
    const locs = [...new Set([...d1.map((d) => d.loc).filter(Boolean), ...d2.map((d) => d.loc).filter(Boolean)])].slice(0, 12);
    return locs.map((loc) => {
      const locationRows = [...d1, ...d2].filter((d) => d.loc === loc);
      const above = locationRows.filter((d) => d.status === "Above Max").length;
      const below = locationRows.filter((d) => d.status === "Below Min").length;
      const g1Breaches = d1.filter((d) => d.loc === loc && d.status !== "Normal").length;
      const g2Breaches = d2.filter((d) => d.loc === loc && d.status !== "Normal").length;

      return {
        loc: loc.length > 12 ? loc.slice(0, 11) + "…" : loc,
        above,
        below,
        "G1 Breaches": g1Breaches,
        "G2 Breaches": g2Breaches,
        total: above + below,
      };
    })
      .filter((row) => row.total > 0 || row["G1 Breaches"] > 0 || row["G2 Breaches"] > 0)
      .sort((a, b) => (compareBy === "Location"
        ? b.total - a.total || b.above - a.above || a.loc.localeCompare(b.loc)
        : (b["G1 Breaches"] + b["G2 Breaches"]) - (a["G1 Breaches"] + a["G2 Breaches"]) || a.loc.localeCompare(b.loc)))
      .slice(0, 10);
  };
  const buildTrendChart = (d1, d2) => monthOptions.map((m) => ({ month: m, "G1 Deviations": d1.filter((d) => d.month === m && d.status !== "Normal").length, "G2 Deviations": d2.filter((d) => d.month === m && d.status !== "Normal").length }));

  const kpiChart   = useMemo(() => buildKpiChart(g1Data, g2Data), [g1Data, g2Data]); // eslint-disable-line
  const locChart   = useMemo(() => buildLocChart(g1Data, g2Data), [g1Data, g2Data]); // eslint-disable-line
  const trendChart = useMemo(() => buildTrendChart(g1Data, g2Data), [g1Data, g2Data]); // eslint-disable-line

  const statRow = (d, label, color) => ({ label, color, above: d.filter((x) => x.status === "Above Max").length, below: d.filter((x) => x.status === "Below Min").length, normal: d.filter((x) => x.status === "Normal").length, total: d.length });
  const g1Stats = g1 ? statRow(g1Data, g1, "#2b7a9e") : null;
  const g2Stats = g2 ? statRow(g2Data, g2, "#d97706") : null;
  const statusChart = useMemo(() => ([
    { status: "Above Max",    "Group 1": g1Stats?.above  ?? 0, "Group 2": g2Stats?.above  ?? 0 },
    { status: "Below Min",    "Group 1": g1Stats?.below  ?? 0, "Group 2": g2Stats?.below  ?? 0 },
    { status: "Within Range", "Group 1": g1Stats?.normal ?? 0, "Group 2": g2Stats?.normal ?? 0 },
  ]), [g1Stats, g2Stats]);
  const statusComparisonChartData = useMemo(() => ({
    categories: statusChart.map((row) => row.status),
    series: [
      { name: "Group 1", data: statusChart.map((row) => row["Group 1"]), color: "#5b8def" },
      { name: "Group 2", data: statusChart.map((row) => row["Group 2"]), color: "#4cc39a" },
    ],
    colors: ["#5b8def", "#4cc39a"],
    unitsMap: {},
  }), [statusChart]);
  const trendComparisonChartData = useMemo(() => ({
    categories: trendChart.map((row) => row.month),
    series: [
      { name: `${g1} Deviations`, data: trendChart.map((row) => row["G1 Deviations"]), color: C.accent },
      { name: `${g2} Deviations`, data: trendChart.map((row) => row["G2 Deviations"]), color: C.amber },
    ],
    colors: [C.accent, C.amber],
    unitsMap: {},
  }), [trendChart, g1, g2]);
  const kpiComparisonChartData = useMemo(() => ({
    categories: kpiChart.map((row) => row.kpi),
    series: [
      { name: `${g1} Above`, data: kpiChart.map((row) => row["G1 Above"]), color: C.accent },
      { name: `${g1} Below`, data: kpiChart.map((row) => row["G1 Below"]), color: "#60a5fa" },
      { name: `${g2} Above`, data: kpiChart.map((row) => row["G2 Above"]), color: C.amber },
      { name: `${g2} Below`, data: kpiChart.map((row) => row["G2 Below"]), color: "#fcd34d" },
    ].filter((series) => series.data.some((value) => Number(value || 0) !== 0)),
    colors: [C.accent, "#60a5fa", C.amber, "#fcd34d"],
    unitsMap: {},
  }), [kpiChart, g1, g2]);
  const locationComparisonChartData = useMemo(() => ({
    categories: locChart.map((row) => row.loc),
    series: compareBy === "Location"
      ? [
        { name: "Above Max", data: locChart.map((row) => row.above), color: C.red },
        { name: "Below Min", data: locChart.map((row) => row.below), color: C.amber },
      ]
      : [
        { name: g1, data: locChart.map((row) => row["G1 Breaches"]), color: C.accent },
        { name: g2, data: locChart.map((row) => row["G2 Breaches"]), color: C.amber },
      ],
    colors: compareBy === "Location" ? [C.red, C.amber] : [C.accent, C.amber],
    stacked: false,
    showCategoryLegend: false,
    unitsMap: {},
  }), [locChart, compareBy, g1, g2]);

  return (
    <div>
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 16, padding: "24px 28px 20px", marginBottom: 20, boxShadow: "0 1px 6px #0000000a" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Filters</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>Compare by:</span>
            <div style={{ display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 8, padding: 3 }}>
              {["Financial Year", "Period", "Location"].map((opt) => (
                <button key={opt} onClick={() => setCompareBy(opt)} style={{ padding: "5px 13px", borderRadius: 6, border: "none", background: compareBy === opt ? C.accent : "transparent", color: compareBy === opt ? "#fff" : C.sub, fontSize: 12, fontWeight: compareBy === opt ? 600 : 400, cursor: "pointer", transition: "all .15s", whiteSpace: "nowrap" }}>{opt}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
          {compareBy !== "Financial Year" && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 180 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, height: 22, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.sub }}>Financial Year</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.accent, background: "#e0f2fe", borderRadius: 4, padding: "1px 6px", textTransform: "uppercase" }}>common</span>
              </div>
              <Dropdown label="Financial Year" options={fyOptions} value={commonFY || defaultFY} onChange={handleCommonFYChange} placeholder="Select Financial Year" width={260} />
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", flex: 2, minWidth: 280 }}>
            <div style={{ display: "flex", alignItems: "center", height: 22, marginBottom: 6, gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: C.sub }}>{compareBy}</span>
              <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: C.accent, textAlign: "center" }}>Group 1</span>
                <span style={{ width: 30 }} />
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: C.amber, textAlign: "center" }}>Group 2</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}><Dropdown label={compareBy} options={compareOptions} value={g1} onChange={setG1} placeholder="Select..." width={200} /></div>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, flexShrink: 0 }}>vs</span>
              <div style={{ flex: 1 }}><Dropdown label={compareBy} options={compareOptions} value={g2} onChange={setG2} placeholder="Select..." width={200} /></div>
            </div>
          </div>
          {compareBy !== "Location" && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 180 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, height: 22, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.sub }}>Location</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.accent, background: "#e0f2fe", borderRadius: 4, padding: "1px 6px", textTransform: "uppercase" }}>common</span>
              </div>
              <MultiSelect label="Location" options={locationOptions} value={commonLoc} onChange={setCommonLoc} placeholder="All Locations" pinToBottom={PINNED_LOCATION_CODES} />
            </div>
          )}
          {compareBy !== "Period" && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 180 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, height: 22, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.sub }}>Period</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.accent, background: "#e0f2fe", borderRadius: 4, padding: "1px 6px", textTransform: "uppercase" }}>common</span>
              </div>
              <MultiSelect label="Period" options={monthOptions} value={commonPeriod} onChange={setCommonPeriod} placeholder="All Periods" />
            </div>
          )}
        </div>
      </div>

      {(!g1 || !g2) && (
        <div style={{ background: "#fff", border: `2px dashed ${C.border}`, borderRadius: 14, padding: "50px 24px", textAlign: "center", color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚖️</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Select Group 1 and Group 2 to compare</div>
          <div style={{ fontSize: 12 }}>Choose two {compareBy.toLowerCase()}s above to see the comparison charts</div>
        </div>
      )}

      {g1 && g2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[g1Stats, g2Stats].map((gs, gi) => gs && (
              <div key={gi} style={{ background: "#fff", border: `1.5px solid ${gi === 0 ? C.accent + "66" : C.amber + "66"}`, borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 6px #0000000a" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: gs.color }} />
                  <span style={{ fontSize: 13, fontWeight: 800, color: gs.color }}>Group {gi + 1}: {gs.label}</span>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>{gs.total.toLocaleString()} records</span>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[{ l: "Above Max", v: gs.above, c: C.red, bg: C.redLight, ico: "▲" }, { l: "Below Min", v: gs.below, c: C.amber, bg: C.amberLight, ico: "▼" }, { l: "Normal", v: gs.normal, c: C.green, bg: C.greenLight, ico: "●" }].map((s) => (
                    <div key={s.l} style={{ flex: 1, background: s.bg, borderRadius: 10, padding: "10px 12px", border: `1px solid ${s.c}33`, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: s.c, fontWeight: 700, marginBottom: 4 }}>{s.ico} {s.l}</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: s.c }}>{s.v.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 6px #0000000a" }}>
            {loading ? <ChartLoading height={260} /> : compareBy === "Financial Year" ? (
              <>
                <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Trigger Status Comparison — Financial Year</h3>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>Compare the selected financial years across Above Max, Below Min and Within Range</p>
                <SharedBarChart chartData={statusComparisonChartData} chartOptions={{ height: 320, toolbarVisible: false, transform: false, columnWidth: "28%" }} />
              </>
            ) : (
              <>
                <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>{compareBy === "Period" ? "Deviation Trend — Period Comparison" : `Deviation Trend — ${compareBy} Comparison`}</h3>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>Monthly deviation count: <span style={{ color: C.accent, fontWeight: 700 }}>{g1}</span> vs <span style={{ color: C.amber, fontWeight: 700 }}>{g2}</span></p>
                <SharedBarChart chartData={trendComparisonChartData} chartOptions={{ height: 260, toolbarVisible: false, transform: false, columnWidth: "42%" }} />
              </>
            )}
          </div>

          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 6px #0000000a" }}>
            {loading ? <ChartLoading height={280} /> : (
              <>
                <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>KPI-wise Deviation Comparison</h3>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>Breach count per KPI: <span style={{ color: C.accent, fontWeight: 700 }}>{g1}</span> vs <span style={{ color: C.amber, fontWeight: 700 }}>{g2}</span></p>
                <SharedBarChart chartData={kpiComparisonChartData} chartOptions={{ height: 300, toolbarVisible: false, transform: false, columnWidth: "30%" }} />
              </>
            )}
          </div>

          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 6px #0000000a" }}>
            {loading ? <ChartLoading height={260} /> : (
              <>
                <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Location-wise Deviation Comparison</h3>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>
                  {compareBy === "Location"
                    ? <>Above and Below breach counts by location: <span style={{ color: C.accent, fontWeight: 700 }}>{g1}</span> vs <span style={{ color: C.amber, fontWeight: 700 }}>{g2}</span></>
                    : <>Top 10 locations by total breaches: <span style={{ color: C.accent, fontWeight: 700 }}>{g1}</span> vs <span style={{ color: C.amber, fontWeight: 700 }}>{g2}</span></>}
                </p>
                {locChart.length === 0
                  ? <div style={{ padding: "22px 0", textAlign: "center", fontSize: 12, color: C.muted }}>No location comparison data found.</div>
                  : <SharedHorizontalBarChart chartData={locationComparisonChartData} chartOptions={{ height: Math.max(280, locChart.length * 62 + 110), toolbarVisible: false, transform: false, stacked: false, barHeight: 42, yAxisLabelWidth: 140 }} />}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const buildHorizontalChartData = (rows, labelKey) => {
  const data = rows.filter((r) => r.above || r.below || r.normal);
  return {
    categories: data.map((r) => r[labelKey]),
    series: [
      { name: "Above Max",    data: data.map((r) => r.above  || 0), color: C.red },
      { name: "Below Min",    data: data.map((r) => r.below  || 0), color: C.amber },
      { name: "Within Range", data: data.map((r) => r.normal || 0), color: C.green },
    ],
    colors: [C.red, C.amber, C.green],
    stacked: true,
    showCategoryLegend: false,
    unitsMap: {},
  };
};

export default function DeviationDashboard() {
  const [mainView,  setMainView]  = useState("Financial Year View");
  const [activeTab, setActiveTab] = useState("overview");
  const [compareBy, setCompareBy] = useState("Financial Year");
  const [compareG1, setCompareG1] = useState(null);
  const [compareG2, setCompareG2] = useState(null);
  const [compareCommonLocs, setCompareCommonLocs] = useState([]);
  const [compareCommonPeriods, setCompareCommonPeriods] = useState([]);
  const [compareCommonFY, setCompareCommonFY] = useState(null);

  const configuredModules = deviationConfig.modules;
  const financialYears    = useFinancialYears();
  const sources           = useSources();

  const fyOptions = useMemo(() => financialYears.map((fy) => fy.financial_year_value), [financialYears]);
  const currentFYValue = useMemo(() => fyOptions[0] || null, [fyOptions]);
  const sourceOptions = useMemo(() => sources.map((s) => ({ id: String(s.id), label: s.unitCode || `${s.location?.area ?? ""} ${s.location?.city ?? ""}`.trim() || String(s.id) })), [sources]);
  const sourceLabelById = useMemo(() => Object.fromEntries(sourceOptions.map((s) => [s.id, s.label])), [sourceOptions]);
  const locationLabels  = useMemo(() => sourceOptions.map((s) => s.label), [sourceOptions]);

  const [selectedModules, setSelectedModules] = useState(() => [...configuredModules]);

  const [selLocLabels, setSelLocLabels] = useState([]);
  const [selMonths,    setSelMonths]    = useState([...MONTHS]);
  const [selFY,        setSelFY]        = useState(null);

  const selLocIds = useMemo(() => selLocLabels.map((l) => sourceOptions.find((s) => s.label === l)?.id).filter(Boolean), [selLocLabels, sourceOptions]);
  const compareCommonLocIds = useMemo(() => compareCommonLocs.map((l) => sourceOptions.find((s) => s.label === l)?.id).filter(Boolean), [compareCommonLocs, sourceOptions]);
  const compareLocationGroupIds = useMemo(() => [compareG1, compareG2].map((l) => sourceOptions.find((s) => s.label === l)?.id).filter(Boolean), [compareG1, compareG2, sourceOptions]);

  useEffect(() => { if (currentFYValue && !selFY) setSelFY(currentFYValue); }, [currentFYValue, selFY]);
  useEffect(() => { if (locationLabels.length && !selLocLabels.length) setSelLocLabels([...locationLabels]); }, [locationLabels]); // eslint-disable-line
  useEffect(() => {
    if (!currentFYValue) return;
    if (!compareCommonFY) setCompareCommonFY(currentFYValue);
  }, [currentFYValue, compareCommonFY]);

  useEffect(() => {
    if (!locationLabels.length) return;
    setCompareCommonLocs((prev) => { if (!prev.length) return [...locationLabels]; const valid = prev.filter((loc) => locationLabels.includes(loc)); return valid.length ? valid : [...locationLabels]; });
  }, [locationLabels]);

  useEffect(() => {
    setCompareCommonPeriods((prev) => { if (!prev.length) return [...MONTHS]; const valid = prev.filter((month) => MONTHS.includes(month)); return valid.length ? valid : [...MONTHS]; });
  }, []);

  useEffect(() => {
    setCompareG1(null);
    setCompareG2(null);
    setCompareCommonFY(currentFYValue);
    setCompareCommonLocs([...locationLabels]);
    setCompareCommonPeriods([...MONTHS]);
  }, [compareBy, locationLabels, currentFYValue]);

  const apiFilters = useMemo(() => {
    if (mainView === "Comparison View") {
      if (compareBy === "Financial Year") return { financialYears: [compareG1, compareG2].filter(Boolean), locationIds: compareCommonLocIds, months: compareCommonPeriods };
      if (compareBy === "Period") return { financialYears: compareCommonFY ? [compareCommonFY] : [], locationIds: compareCommonLocIds, months: [compareG1, compareG2].filter(Boolean) };
      return { financialYears: compareCommonFY ? [compareCommonFY] : [], locationIds: compareLocationGroupIds, months: compareCommonPeriods };
    }
    return { financialYears: selFY ? [selFY] : [], locationIds: selLocIds, months: selMonths };
  }, [mainView, compareBy, selFY, selLocIds, selMonths, compareG1, compareG2, compareCommonFY, compareCommonLocIds, compareCommonPeriods, compareLocationGroupIds]);

  // ── filtersReady: removed selLocLabels.length requirement ─────
  // This was blocking the fetch when user deselected all locations.
  // The API call handles empty locationIds correctly (returns all).
  const filtersReady = useMemo(() => {
    if (!configuredModules.length) return false;
    if (!financialYears.length || !selFY) return false;
    if (!locationLabels.length) return false;
    return true;
  }, [configuredModules.length, financialYears.length, selFY, locationLabels.length]);

  const { records: rawRecords, loading } = useDeviationRecords(selectedModules, apiFilters, sources, filtersReady);
  const filtered = useMemo(() => {
    if (!selectedModules.length) return [];
    if (selectedModules.length === configuredModules.length) return rawRecords;
    const moduleSet = new Set(selectedModules.map((m) => m.toLowerCase()));
    return rawRecords.filter((r) => moduleSet.has((r.module || '').toLowerCase()));
  }, [rawRecords, selectedModules, configuredModules.length]);

  const runningThresholdRows = useMemo(() => buildRunningThresholdRows(filtered), [filtered]);
  const totalAnsweredCount = runningThresholdRows.length;
  const { aboveCount, belowCount, normalCount, deviatingRunningRows } = useMemo(() => {
    let aboveCount = 0, belowCount = 0, normalCount = 0;
    const deviatingRunningRows = [];
    runningThresholdRows.forEach((d) => {
      if (d.status === "Above Max")      { aboveCount++;  deviatingRunningRows.push(d); }
      else if (d.status === "Below Min") { belowCount++;  deviatingRunningRows.push(d); }
      else                                 normalCount++;
    });
    return { aboveCount, belowCount, normalCount, deviatingRunningRows };
  }, [runningThresholdRows]);

  const locWithDev        = useMemo(() => new Set(deviatingRunningRows.map((d) => d.locId).filter(Boolean)).size, [deviatingRunningRows]);

  const top5Locs = useMemo(() => {
    const m = {};
    deviatingRunningRows.forEach((d) => {
      const label = sourceLabelById[d.locId] || d.locId || "Unknown";
      if (!m[d.locId]) m[d.locId] = { key: label, above: 0, below: 0, total: 0 };
      if (d.status === "Above Max") { m[d.locId].above++; m[d.locId].total++; }
      else if (d.status === "Below Min") { m[d.locId].below++; m[d.locId].total++; }
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [deviatingRunningRows, sourceLabelById]);

  const locationGrouped = useMemo(() => {
    const grouped = {};
    runningThresholdRows.forEach((row) => {
      const locId = row.locId || "unknown";
      const label = sourceLabelById[locId] || row.loc || locId || "Unknown";
      if (!grouped[locId]) grouped[locId] = { locId, loc: label, above: 0, below: 0, normal: 0, total: 0 };
      if (row.status === "Above Max") grouped[locId].above++;
      else if (row.status === "Below Min") grouped[locId].below++;
      else grouped[locId].normal++;
      grouped[locId].total++;
    });
    return Object.values(grouped).sort((a, b) => b.total - a.total || a.loc.localeCompare(b.loc));
  }, [runningThresholdRows, sourceLabelById]);

  const overviewLocationData = useMemo(() => locationGrouped.slice(0, 5), [locationGrouped]);
  const locationChartData    = useMemo(() =>
    locationGrouped.slice(0, 10).map((r) => ({ ...r, loc: r.loc.length > 14 ? `${r.loc.slice(0, 13)}…` : r.loc }))
  , [locationGrouped]);

  const kpiGrouped = useMemo(() => {
    const grouped = {};
    runningThresholdRows.forEach((row) => {
      const key = String(row.kpi || "").trim() || "Unknown";
      if (!grouped[key]) grouped[key] = { key, above: 0, below: 0, normal: 0, total: 0 };
      if (row.status === "Above Max") grouped[key].above++;
      else if (row.status === "Below Min") grouped[key].below++;
      else grouped[key].normal++;
      grouped[key].total++;
    });
    return Object.values(grouped);
  }, [runningThresholdRows]);

  const overviewKpiData = useMemo(() =>
    kpiGrouped
      .filter((r) => r.above > 0 || r.below > 0)
      .sort((a, b) => (b.above + b.below) - (a.above + a.below) || a.key.localeCompare(b.key))
      .slice(0, 5)
      .map((r) => ({ ...r, kpi: r.key }))
  , [kpiGrouped]);

  const kpiTopLists = useMemo(() => ({
    above: kpiGrouped
      .filter((r) => r.above > 0)
      .sort((a, b) => b.above - a.above || b.total - a.total || a.key.localeCompare(b.key))
      .map((r) => ({ ...r, total: r.above }))
      .slice(0, 5),
    below: kpiGrouped
      .filter((r) => r.below > 0)
      .sort((a, b) => b.below - a.below || b.total - a.total || a.key.localeCompare(b.key))
      .map((r) => ({ ...r, total: r.below }))
      .slice(0, 5),
  }), [kpiGrouped]);

  const kpiDeviationLeaders = useMemo(() =>
    kpiGrouped
      .map((r) => ({ ...r, rate: r.total ? Math.round(((r.above + r.below) / r.total) * 100) : 0 }))
      .filter((r) => r.above > 0 || r.below > 0)
      .sort((a, b) => b.rate - a.rate || (b.above + b.below) - (a.above + a.below) || a.key.localeCompare(b.key))
      .slice(0, 5)
  , [kpiGrouped]);

  const kpiDevCount = useMemo(() => new Set(
    deviatingRunningRows
      .map((row) => String(row.kpi || "").trim())
      .filter(Boolean)
  ).size, [deviatingRunningRows]);

  const alertLogRows = useMemo(() =>
    runningThresholdRows
      .filter((d) => d.hasThreshold && (d.status === "Above Max" || d.status === "Below Min"))
      .map((d) => ({
        loc: String(d.loc || sourceLabelById[d.locId] || d.locId || "Unknown"),
        locId: String(d.locId || ""),
        module: d.module || "—",
        month: d.month || "—",
        subCategory: d.subCategory || "—",
        kpi: d.kpi || "Unknown",
        min: d.min,
        max: d.max,
        actual: d.runningValue ?? d.actual,
        deviation: d.status === "Above Max"
          ? Math.abs((d.runningValue ?? d.actual ?? 0) - (d.max ?? 0))
          : Math.abs((d.min ?? 0) - (d.runningValue ?? d.actual ?? 0)),
        status: d.status,
      }))
      .sort((a, b) =>
        a.loc.localeCompare(b.loc) || a.month.localeCompare(b.month) ||
        a.kpi.localeCompare(b.kpi) || a.subCategory.localeCompare(b.subCategory)
      )
  , [runningThresholdRows, sourceLabelById]);

  const periodBarData = useMemo(() => {
    const monthCounts = Object.fromEntries(
      selMonths.map((month) => [month, { month, above: 0, below: 0, normal: 0 }])
    );

    runningThresholdRows.forEach((row) => {
      const month = row.month;
      if (!monthCounts[month]) return;

      if (row.status === "Above Max") monthCounts[month].above += 1;
      else if (row.status === "Below Min") monthCounts[month].below += 1;
      else if (row.status === "Normal") monthCounts[month].normal += 1;
    });

    return selMonths
      .map((month) => monthCounts[month])
      .filter(Boolean);
  }, [runningThresholdRows, selMonths]);



  const locationStatusPanels = useMemo(() => {
    const build = (status) => {
      const grouped = {};
      deviatingRunningRows.filter((d) => d.status === status).forEach((d) => {
        const key = d.locId || "unknown";
        if (!grouped[key]) grouped[key] = { locId: key, loc: sourceLabelById[key] || key || "Unknown", count: 0, kpis: new Set() };
        grouped[key].count++;
        if (d.kpi) grouped[key].kpis.add(d.kpi);
      });
      return Object.values(grouped).map((row) => ({ ...row, kpis: [...row.kpis] })).sort((a, b) => b.count - a.count);
    };
    return { above: build("Above Max"), below: build("Below Min") };
  }, [deviatingRunningRows, sourceLabelById]);

  const donutTotal = aboveCount + belowCount + normalCount;
  const donutChartData = useMemo(() => {
    const items = [
      { name: "Above Max Trigger", value: aboveCount, color: C.red },
      { name: "Below Min Trigger", value: belowCount, color: C.amber },
      { name: "Within Range",      value: normalCount, color: C.green },
    ];
    return {
      labels:   items.map((i) => i.name),
      values:   items.map((i) => i.value),
      colors:   items.map((i) => i.color),
      unitsMap: {},
      series:   items.map((i) => ({ name: i.name, data: [i.value], color: i.color })),
    };
  }, [aboveCount, belowCount, normalCount]);
  const periodDeviationChartData = useMemo(() => buildHorizontalChartData(periodBarData, "month"), [buildHorizontalChartData, periodBarData]);
  const locationDeviationBarChartData = useMemo(() => ({
    categories: locationChartData.map((row) => row.loc),
    series: [
      { name: "Above Max Trigger", data: locationChartData.map((row) => row.above || 0), color: C.red },
      { name: "Below Min Trigger", data: locationChartData.map((row) => row.below || 0), color: C.amber },
      { name: "Within Range", data: locationChartData.map((row) => row.normal || 0), color: C.green },
    ],
    colors: [C.red, C.amber, C.green],
    unitsMap: {},
  }), [locationChartData]);
  const heatmapRows = useMemo(() => runningThresholdRows
    .filter((d) => d.loc && d.kpi), [runningThresholdRows]);

  const resetAll = () => {
    setSelectedModules([...configuredModules]);
    setSelLocLabels([...locationLabels]);
    setSelMonths([...MONTHS]);
    setSelFY(currentFYValue);
  };

  const TABS = [
    { id: "overview",  label: "Overview"       },
    { id: "locations", label: "▲▼ Locations"   },
    { id: "kpis",      label: "KPI Deviation"  },
    { id: "heatmap",   label: "Location × KPI" },
    { id: "alerts",    label: "Alert Log"      },
  ];
  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background: "#fff", borderBottom: `1px solid ${C.border}`, padding: "0 24px", display: "flex", alignItems: "center", gap: 20, boxShadow: "0 1px 4px #0000000a", position: "sticky", top: 0, zIndex: 100, minHeight: 64 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>📊</div>
        <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: "auto" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Modules</span>
          <MultiSelect label="Modules" options={configuredModules} value={selectedModules} onChange={setSelectedModules} placeholder="No Modules" showCount />
        </div>
        <div style={{ display: "flex", gap: 8, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 14, padding: 6, boxShadow: "0 1px 4px #00000008" }}>
          {["Financial Year View", "Comparison View"].map((view) => {
            const active = mainView === view;
            return (
              <button
                key={view}
                type="button"
                onClick={() => setMainView(view)}
                style={{
                  padding: "10px 22px",
                  borderRadius: 11,
                  border: "none",
                  background: active ? C.accent : "#fff",
                  color: active ? "#fff" : C.sub,
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                  cursor: "pointer",
                  boxShadow: active ? "0 6px 16px rgba(43,122,158,0.22)" : "none",
                  transition: "all .16s ease",
                }}
              >
                {view}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: "20px 24px" }}>
        {mainView === "Comparison View" && (
          <ComparisonView
            data={filtered}
            compareBy={compareBy} setCompareBy={setCompareBy}
            fyOptions={fyOptions} defaultFY={currentFYValue} locationOptions={locationLabels} monthOptions={MONTHS}
            g1={compareG1} setG1={setCompareG1} g2={compareG2} setG2={setCompareG2}
            commonLoc={compareCommonLocs} setCommonLoc={setCompareCommonLocs}
            commonPeriod={compareCommonPeriods} setCommonPeriod={setCompareCommonPeriods}
            commonFY={compareCommonFY} setCommonFY={setCompareCommonFY}
            loading={loading}
          />
        )}

        {mainView === "Financial Year View" && (
          <>
            <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 16, padding: "24px 28px 20px", marginBottom: 18, boxShadow: "0 1px 6px #0000000a" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Filters</span>
                <button onClick={resetAll} style={{ padding: "5px 12px", border: `1px solid ${C.border}`, borderRadius: 6, background: "#f8fafc", color: C.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Reset All</button>
              </div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.sub, marginBottom: 6 }}>Financial Year</div>
                  <Dropdown label="Financial Year" options={fyOptions} value={selFY} onChange={setSelFY} placeholder="Select Financial Year" width={260} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.sub, marginBottom: 6 }}>Location</div>
                  <MultiSelect label="Location" options={locationLabels} value={selLocLabels} onChange={setSelLocLabels} placeholder="All Locations" pinToBottom={PINNED_LOCATION_CODES} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.sub, marginBottom: 6 }}>Period</div>
                  <MultiSelect label="Period" options={MONTHS} value={selMonths} onChange={setSelMonths} placeholder="All Periods" />
                </div>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12, alignItems: "center" }}>
                {selectedModules.length > 0 && selectedModules.length < configuredModules.length && <span style={{ background: C.accentLight, color: C.accent, fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 9px", border: `1px solid ${C.accent}33` }}>🏭 {selectedModules.join(", ")}</span>}
                {selLocLabels.length  < locationLabels.length && <span style={{ background: C.accentLight, color: C.accent, fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 9px", border: `1px solid ${C.accent}33` }}>📍 {selLocLabels.length} location{selLocLabels.length > 1 ? "s" : ""}</span>}
                {selMonths.length     < MONTHS.length          && <span style={{ background: C.accentLight, color: C.accent, fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 9px", border: `1px solid ${C.accent}33` }}>📅 {selMonths.join(", ")}</span>}
                {selFY && <span style={{ background: C.accentLight, color: C.accent, fontSize: 9, fontWeight: 700, borderRadius: 20, padding: "2px 9px", border: `1px solid ${C.accent}33` }}>📆 {selFY}</span>}
                <span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>{loading ? "Loading…" : `${filtered.length.toLocaleString()} records match`}</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
              <StatCard loading={loading} label="Total Answered" value={totalAnsweredCount.toLocaleString()} sub="KPIs with defined triggers" color={C.accent} bg={C.accentLight} icon="📋" />
              <StatCard loading={loading} label="Above Max Trigger"   value={aboveCount.toLocaleString()}       sub="Exceeded upper limit"          color={C.red}    bg={C.redLight}     icon="🔴" />
              <StatCard loading={loading} label="Within Range"        value={normalCount.toLocaleString()}      sub="No trigger breach"            color={C.green}  bg={C.greenLight}   icon="🟢" />
              <StatCard loading={loading} label="Below Min Trigger"   value={belowCount.toLocaleString()}       sub="Below lower limit"             color={C.amber}  bg={C.amberLight}   icon="🟠" />
              <StatCard loading={loading} label="Locations Deviating" value={locWithDev}                          sub={`of ${selLocLabels.length} selected`} color={C.teal} bg={C.tealLight} icon="📍" />
              <StatCard loading={loading} label="KPIs Deviating"      value={kpiDevCount}                                                                    color={C.slate} bg={C.slateLight}  icon="⚡" />
            </div>

            <div style={{ display: "flex", gap: 2, marginBottom: 18, borderBottom: `2px solid ${C.border}`, overflowX: "auto" }}>
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: "10px 18px", background: activeTab === t.id ? "#fff" : "transparent", border: "none", borderBottom: `2px solid ${activeTab === t.id ? C.accent : "transparent"}`, marginBottom: -2, cursor: "pointer", fontFamily: "inherit", color: activeTab === t.id ? C.accent : C.muted, fontSize: 12, fontWeight: activeTab === t.id ? 800 : 500, borderRadius: "8px 8px 0 0", whiteSpace: "nowrap", transition: "all .15s" }}>{t.label}</button>
              ))}
            </div>

            {activeTab === "overview" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Deviation Status Distribution</h3>
                  <p style={{ margin: "0 0 10px", fontSize: 11, color: C.muted }}>Proportion of records by trigger status</p>
                  <div style={{ flex: 1 }}>
                    {loading ? <ChartLoading height={230} /> : (
                      donutTotal
                        ? <SharedDonutChart chartData={donutChartData} chartOptions={{ height: 250, toolbarVisible: false, transform: false, donutSize: "62%" }} />
                        : <div style={{ minHeight: 230, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: C.muted }}>No deviation status data found.</div>
                    )}
                  </div>
                </div>
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Period Deviations</h3>
                  <p style={{ margin: "0 0 10px", fontSize: 11, color: C.muted }}>Above, Below and Within counts by selected period</p>
                  <div style={{ flex: 1 }}>
                    {loading ? <ChartLoading height={230} /> : (
                      periodDeviationChartData.categories.length
                        ? <SharedHorizontalBarChart chartData={periodDeviationChartData} chartOptions={{ height: 260, toolbarVisible: false, transform: false, stacked: true, barHeight: 48, yAxisLabelWidth: 90 }} />
                        : <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: C.muted }}>No period deviation data found.</div>
                    )}
                  </div>
                </div>
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Location Deviations</h3>
                  <p style={{ margin: "0 0 12px", fontSize: 11, color: C.muted }}>{`Top ${Math.min(overviewLocationData.length, 5)} locations by total trigger breaches`}</p>
                  <div style={{ flex: 1 }}>
                    {loading ? <ChartLoading height={230} /> : (
                      <BreachBarList rows={overviewLocationData} labelKey="loc" emptyText="No deviating locations found." />
                    )}
                  </div>
                </div>
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a", display: "flex", flexDirection: "column" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>KPI Breach Frequency</h3>
                  <p style={{ margin: "0 0 12px", fontSize: 11, color: C.muted }}>{`Top ${Math.min(overviewKpiData.length, 5)} KPIs by trigger counts`}</p>
                  <div style={{ flex: 1 }}>
                    {loading ? <ChartLoading height={230} /> : (
                      <BreachBarList rows={overviewKpiData} labelKey="kpi" emptyText="No deviating KPIs found." />
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "locations" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                  <Top5 title="🏆 Top 5 Locations — Above Max" subtitle="Most breaches of upper trigger limit" items={top5Locs.filter((l) => l.above > 0).map((l) => ({ key: l.key, above: l.above, total: l.above }))} color={C.red}   bg={C.redLight}   />
                  <Top5 title="🏆 Top 5 Locations — Below Min" subtitle="Most breaches of lower trigger limit" items={top5Locs.filter((l) => l.below > 0).map((l) => ({ key: l.key, below: l.below, total: l.below }))} color={C.amber} bg={C.amberLight} />
                </div>
                {[
                  { status: "Above Max", color: C.red,   bg: C.redLight,   label: "▲ EXCEEDS MAX TRIGGER",     count: aboveCount, rows: locationStatusPanels.above, badge: "Alert auto-sent" },
                  { status: "Below Min", color: C.amber, bg: C.amberLight, label: "▼ FALLS BELOW MIN TRIGGER", count: belowCount, rows: locationStatusPanels.below, badge: "Alert auto-sent" },
                ].map(({ status, color, bg, label, count, rows, badge }) => (
                  <div key={status} style={{ background: "#fff", border: `1.5px solid ${color}44`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                      <span style={{ background: bg, color, fontWeight: 800, borderRadius: 8, padding: "4px 12px", fontSize: 12 }}>{label}</span>
                      <span style={{ fontSize: 11, color: C.muted }}>{count} breaches · {rows.length} locations · <span style={{ color, fontWeight: 600 }}>{badge}</span></span>
                    </div>
                    {loading ? <ChartLoading height={120} /> : (
                      <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
                        {rows.length === 0 ? <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "28px 0" }}>No locations found for this status.</div>
                          : rows.map((r, i) => (
                          <div key={r.locId} style={{ display: "grid", gridTemplateColumns: "42px minmax(180px, 240px) 1fr 56px", alignItems: "center", gap: 12, padding: "9px 12px", background: i < 3 ? bg : "#fff", borderRadius: 10, border: `1px solid ${color}22` }}>
                            <span style={{ fontSize: 11, fontWeight: 900, color, width: 30, textAlign: "center", flexShrink: 0 }}>#{i + 1}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.loc}</span>
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              {r.kpis.map((k) => <span key={k} style={{ background: "#fff", color, border: `1px solid ${color}33`, borderRadius: 20, padding: "2px 8px", fontSize: 9, fontWeight: 700 }}>{k}</span>)}
                            </div>
                            <span style={{ fontSize: 12, color, fontWeight: 800, whiteSpace: "nowrap", textAlign: "right" }}>{status === "Above Max" ? "▲" : "▼"} {r.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>All Locations — Deviation Bar Chart</h3>
                  <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>{`Top ${Math.min(locationChartData.length, 10)} locations by trigger status counts`}</p>
                  {loading ? <ChartLoading height={300} /> : (
                    locationDeviationBarChartData.categories.length
                      ? <SharedBarChart chartData={locationDeviationBarChartData} chartOptions={{ height: 300, toolbarVisible: false, transform: false, columnWidth: "46%" }} />
                      : <div style={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: C.muted }}>No location deviation data found.</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "kpis" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                  <Top5
                    title="🏆 Top 5 KPIs — Above Max"
                    subtitle="Most above-max breaches"
                    items={kpiTopLists.above.map((row) => ({ key: row.key, above: row.above, total: row.above }))}
                    color={C.red}
                    bg={C.redLight}
                  />
                  <Top5
                    title="🏆 Top 5 KPIs — Below Min"
                    subtitle="Most below-min breaches"
                    items={kpiTopLists.below.map((row) => ({ key: row.key, below: row.below, total: row.below }))}
                    color={C.amber}
                    bg={C.amberLight}
                  />
                </div>
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Which KPIs Are Deviating Most?</h3>
                  <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>Deviation rate, breach counts, and affected locations per KPI</p>
                  {loading ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                      {Array.from({ length: 5 }).map((_, i) => <Shimmer key={i} height={86} radius={10} />)}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {kpiDeviationLeaders.map((row) => {
                        const total = Math.max(1, row.total || 0);
                        return (
                          <div key={row.key} style={{ padding: "12px 16px", background: "#fafafa", borderRadius: 10, border: `1px solid ${C.border}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: C.text, flex: 1 }}>{row.key}</span>
                              <span style={{ fontSize: 11, fontWeight: 800, color: C.red, background: C.redLight, borderRadius: 20, padding: "2px 9px" }}>{row.rate}% deviation rate</span>
                            </div>
                            <div style={{ height: 8, background: C.border, borderRadius: 999, overflow: "hidden", display: "flex", marginBottom: 8 }}>
                              {!!row.above && <div style={{ width: `${(row.above / total) * 100}%`, background: C.red }} />}
                              {!!row.below && <div style={{ width: `${(row.below / total) * 100}%`, background: C.amber }} />}
                              {!!row.normal && <div style={{ width: `${(row.normal / total) * 100}%`, background: C.green }} />}
                            </div>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 10, color: C.red, fontWeight: 700 }}>▲ {row.above} above</span>
                              <span style={{ fontSize: 10, color: C.amber, fontWeight: 700 }}>▼ {row.below} below</span>
                              <span style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>● {row.normal} normal</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "heatmap" && (
              <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a" }}>
                <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Location × KPI Deviation Matrix</h3>
                <p style={{ margin: "0 0 10px", fontSize: 11, color: C.muted }}>Dominant trigger status per location-KPI combination</p>
                <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                  {[["▲ Above Max", C.red, C.redLight], ["▼ Below Min", C.amber, C.amberLight], ["● Normal", C.green, C.greenLight]].map(([l, c, b]) => (
                    <div key={l} style={{ background: b, border: `1px solid ${c}44`, borderRadius: 20, padding: "3px 12px" }}><span style={{ fontSize: 11, color: c, fontWeight: 700 }}>{l}</span></div>
                  ))}
                </div>
                {loading ? <ChartLoading height={300} /> : <Heatmap data={heatmapRows} />}
              </div>
            )}

            {activeTab === "alerts" && (
              <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: "0 1px 6px #0000000a" }}>
                <h3 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 800, color: C.sub, textTransform: "uppercase", letterSpacing: 0.6 }}>Alert Log — All Trigger Breaches</h3>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>Module, Category, Sub Category, KPI, actual value, deviation and breach status</p>
                {loading ? <ChartLoading height={300} /> : <AlertTable data={alertLogRows} />}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } } * { box-sizing: border-box; }`}</style>
    </div>
  );
}
